/**
 * English strings. Same keys as pt-BR.js — every key in pt-BR must exist here.
 */
export default {
  'hero.title': 'Shrink. Reduce the size of your images. Free, no limits.',
  'hero.subtitle': 'Reduce file size by up to 80%. PNG, JPEG, WebP. No upload, no login. 100% in your browser.',
  'hero.badge1': '100% free',
  'hero.badge2': 'No quantity limit',
  'hero.badge3': 'No upload — private',
  'hero.badge4': 'No signup',
  'hero.badge5': 'No visible quality loss',

  'header.title': 'Shrink',

  'dropzone.title': 'Drop your images',
  'dropzone.subtitle': 'or click to select',
  'dropzone.meta': 'PNG, JPEG, WebP · up to 20 MB · as many as you want',
  'dropzone.ariaLabel': 'Drop or select images to compress',
  'dropzone.warning': 'Processing {count} files — performance may degrade',
  'dropzone.hint': 'Your files will appear here',

  'fileList.queued': 'Queued',
  'fileList.processing': 'Compressing…',
  'fileList.done': 'Done',
  'fileList.error': 'Error',
  'fileList.noReduction': 'No reduction',
  'fileList.larger': 'Larger',
  'fileList.removeAria': 'Remove {filename}',

  'download.button': 'Download all (ZIP)',
  'download.loading': 'Generating ZIP…',
  'download.empty': 'No files to download yet',
  'download.success': 'ZIP downloaded ({count} files)',

  'progress.processing': 'Compressing {done} of {total}…',
  'progress.doneWithError': '{done} ready, {error} with error',
  'progress.done': '{done} of {total} ready',

  'toast.unsupportedType': '.{ext} format not supported',
  'toast.tooLarge': 'File too large ({size}). Maximum 20 MB',
  'toast.oomError': 'Out of memory. Try smaller batches',
  'toast.encodeError': 'Failed to process image',
  'toast.sanitizeError': 'Invalid filename',

  'features.title': 'Why compress images?',
  'features.subtitle': 'Smaller images mean faster pages, less storage, and frictionless sharing.',
  'features.webTitle': 'Faster web pages',
  'features.webDesc': "Images are the #1 performance bottleneck. Compressing improves Largest Contentful Paint (LCP), boosts Google ranking, and lowers bounce rate.",
  'features.emailTitle': 'Emails that fit the limit',
  'features.emailDesc': "Gmail caps attachments at 25MB. A single uncompressed photo is 8-15MB — you can only fit 2-3. Compressed, you can send entire albums in one email.",
  'features.chatTitle': 'No auto-compression in messages',
  'features.chatDesc': 'WhatsApp, Telegram, and Discord compress images automatically (losing quality). Send the already-optimized version and keep the sharpness.',
  'features.storageTitle': 'Less storage',
  'features.storageDesc': 'Cut down on disk, backup, and cloud usage (Google Drive, iCloud, Dropbox). 1,000 photos at 2MB each, compressed 50%, frees up 1GB.',
  'features.mobileTitle': 'Save mobile data',
  'features.mobileDesc': 'On 4G/5G, a single uncompressed photo can eat your whole data plan. Compressed, you fit 5x more photos in the same data package.',
  'features.costTitle': 'Less bandwidth, less cost',
  'features.costDesc': 'Cloudflare, AWS, Vercel — every byte saved is a cent saved. Sites with 1 TB/month of image traffic can save hundreds of dollars on hosting and CDN.',

  'howItWorks.title': 'How it works',
  'howItWorks.step1Title': 'Drop your images',
  'howItWorks.step1Desc': 'Drag and drop PNG, JPEG, or WebP files. Or click to select. No quantity limit.',
  'howItWorks.step2Title': 'Automatic compression',
  'howItWorks.step2Desc': 'Processing runs directly in your browser via WebAssembly. No upload. Each file shows the original size, the compressed size, and the % saved.',
  'howItWorks.step3Title': 'Download everything as ZIP',
  'howItWorks.step3Desc': 'One click downloads all compressed files in a single ZIP, with original filenames preserved.',

  'footer.disclaimer': 'No login · no upload · 100% in your browser',
  'footer.howItWorks': 'How it works',
  'footer.features': 'Why compress',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'footer.contact': 'Contact',
  'footer.about': 'About',

  // legal pages (Privacy, Terms, Contact, About)
  'legal.lastUpdated': 'Last updated',
  'common.skip': 'Skip to main content',
  'common.backHome': '← Back to Shrink',

  // Privacy Policy
  'privacy.title': 'Privacy Policy',
  'privacy.lead':
    "Shrink is a free image compression tool that runs entirely in your browser. This policy explains, in plain language, what data we collect (almost none), why, and what rights you have.",
  'privacy.s1Title': '1. The short version',
  'privacy.s1Body':
    "We don't see, store, or transmit your images. Compression happens locally in your browser via WebAssembly. We don't require an account, email, or any personal information to use Shrink. The only third-party that may set cookies is Google AdSense, which serves ads to free users.",
  'privacy.s2Title': '2. What we collect',
  'privacy.s2Body': 'By default, we collect nothing about you. Specifically:',
  'privacy.s2Item1': "No images, filenames, or image metadata ever leave your device.",
  'privacy.s2Item2': 'No account, email, or name is required.',
  'privacy.s2Item3': 'No analytics, tracking pixels, or fingerprinting on our side.',
  'privacy.s2Item4':
    'The only data we store locally is your language preference (a single cookie-like value in your browser localStorage), so the site stays in your chosen language next visit. You can clear it any time.',
  'privacy.s3Title': '3. Third-party services: Google AdSense',
  'privacy.s3Body':
    "To keep Shrink free, we display ads served by Google AdSense. AdSense uses cookies to serve ads based on your prior visits to this or other websites. Google's use of advertising cookies enables it and its partners to serve ads based on your visit to this site and/or other sites on the Internet.",
  'privacy.s3Body2':
    'You can opt out of personalized advertising by visiting Google Ads Settings. You can also opt out of certain third-party vendors’ use of cookies for personalized advertising by visiting www.aboutads.info.',
  'privacy.s4Title': '4. Cookies we set',
  'privacy.s4Body':
    'We do not set any first-party tracking or analytics cookies. The only first-party storage we use is your language preference in localStorage. Google AdSense may set cookies under its own privacy policy; see Section 3.',
  'privacy.s5Title': '5. GDPR (European Economic Area)',
  'privacy.s5Body':
    'If you are in the EEA, the legal basis for processing under the General Data Protection Regulation is either your consent (for personalized ads via AdSense) or legitimate interest (for the basic operation of the site). You have the right to access, rectify, erase, restrict, object to, and port your personal data. To exercise these rights, contact us at privacy@goodtool.site.',
  'privacy.s6Title': '6. CCPA / CPRA (California)',
  'privacy.s6Body':
    'If you are a California resident, you have the right to know what personal information is collected, to delete it, and to opt out of its sale or sharing. We do not sell personal information. AdSense may share information with Google for ad personalization; you can opt out via your Google Ads Settings.',
  'privacy.s7Title': '7. LGPD (Brazil)',
  'privacy.s7Body':
    'Se você está no Brasil, a Lei Geral de Proteção de Dados (LGPD) garante os seguintes direitos: confirmação da existência de tratamento, acesso, correção, anonimização, portabilidade, eliminação e revogação do consentimento. Para exercer esses direitos, entre em contato pelo privacy@goodtool.site.',
  'privacy.s8Title': "8. Children's privacy",
  'privacy.s8Body':
    'Shrink is not directed at children under 13, and we do not knowingly collect personal information from children. The service can be used by anyone regardless of age without providing personal data, in line with COPPA and similar laws.',
  'privacy.s9Title': '9. International data transfers',
  'privacy.s9Body':
    'We do not transfer your data because we do not collect it. AdSense data is processed by Google under their privacy policy, which may involve transfer to and processing in the United States and other countries.',
  'privacy.s10Title': '10. Changes to this policy',
  'privacy.s10Body':
    'We may update this policy from time to time. The "Last updated" date at the top of this page reflects when the policy was last revised. Continued use of Shrink after a change indicates acceptance of the updated policy.',
  'privacy.s11Title': '11. Contact',
  'privacy.s11Body':
    'Questions or requests about this policy: privacy@goodtool.site. We respond within a few business days.',

  // Terms of Service
  'terms.title': 'Terms of Service',
  'terms.lead':
    "By using Shrink you agree to these terms. The short version: it's free, it works in your browser, use it responsibly, no warranty.",
  'terms.s1Title': '1. Acceptance',
  'terms.s1Body':
    'By accessing or using Shrink (shrink.goodtool.site), you agree to be bound by these Terms of Service. If you do not agree, do not use the service.',
  'terms.s2Title': '2. Service description',
  'terms.s2Body':
    'Shrink is a free, client-side image compression tool. It runs entirely in your browser using WebAssembly. We do not host, store, or transmit your images. The service is provided "as is" and "as available," without warranty of any kind.',
  'terms.s3Title': '3. User responsibilities',
  'terms.s3Body': 'You agree to use Shrink only for lawful purposes. You will not:',
  'terms.s3Item1': 'Use the service to process images you do not have the right to compress.',
  'terms.s3Item2': 'Attempt to disrupt, overload, or otherwise abuse the service or its infrastructure.',
  'terms.s3Item3': 'Use automated scraping or bulk access beyond normal browser usage.',
  'terms.s4Title': '4. Intellectual property',
  'terms.s4Body':
    "Shrink's source code, design, and branding are owned by the publisher. The service itself is provided to you at no cost. Your images remain yours at all times — we never see, store, or claim any rights over them.",
  'terms.s5Title': '5. Third-party services',
  'terms.s5Body':
    "Shrink displays ads served by Google AdSense. AdSense is governed by Google's own terms and privacy policy. We are not responsible for AdSense content, but we choose it as our ad partner because it offers industry-standard opt-out controls.",
  'terms.s6Title': '6. Disclaimer of warranties',
  'terms.s6Body':
    'The service is provided "as is," without warranty of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and noninfringement. We do not guarantee that the service will be uninterrupted, secure, or error-free, or that compression results will meet your specific requirements.',
  'terms.s7Title': '7. Limitation of liability',
  'terms.s7Body':
    'To the maximum extent permitted by law, the publisher of Shrink shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, arising from your use of the service. If the service caused you direct damages despite the above, our total liability is limited to USD $10.',
  'terms.s8Title': '8. Changes to the terms',
  'terms.s8Body':
    'We may update these terms from time to time. The "Last updated" date at the top of this page reflects the most recent revision. Continued use of Shrink after a change indicates acceptance of the updated terms.',
  'terms.s9Title': '9. Contact',
  'terms.s9Body': 'Questions about these terms: legal@goodtool.site.',

  // Contact page
  'contact.title': 'Contact',
  'contact.lead':
    'Questions, feedback, bug reports, or partnership ideas? Drop us a line. We read everything and respond within a few business days.',
  'contact.emailTitle': 'Email',
  'contact.emailDesc': 'For general questions, feature requests, and bug reports.',
  'contact.privacyTitle': 'Privacy and data requests',
  'contact.privacyDesc':
    'For privacy-related questions, GDPR / CCPA / LGPD requests, or to exercise your data rights.',
  'contact.responseTitle': 'Response time',
  'contact.responseDesc':
    'We aim to reply within 1-3 business days. We are a small team, so thank you for your patience.',

  // About page
  'about.title': 'About Shrink',
  'about.lead':
    'Shrink is a free, no-signup image compressor that runs entirely in your browser. No upload, no account — your files never leave your device. Advertising providers may process data as described in our Privacy Policy.',
  'about.missionTitle': 'Our mission',
  'about.missionBody':
    'Make image optimization fast, private, and free for everyone. We focus on building the best tool we can — no accounts, no upload, no waiting — and we keep it free so anyone can use it, anywhere.',
  'about.whyClientTitle': 'Why client-side?',
  'about.whyClientBody':
    "Because privacy is not a feature you toggle — it is the default. By running in your browser, we make it physically impossible for us to see your images: the network request never happens. This also means faster processing (no upload round-trip) and zero server costs, so the service can stay free forever.",
  'about.whoTitle': 'Who built it',
  'about.whoBody':
    'Shrink is built and maintained by a small team that cares about web performance and user privacy. We built it because we wanted a tool that just works — and we decided to share it with anyone who needs it.',
  'about.fundingTitle': "How it's funded",
  'about.fundingBody':
    'Shrink is free, with no paid plans, no upsells, and no premium tier. It is supported by unobtrusive ads served by Google AdSense. If you would like to support the project in other ways, get in touch via contact@goodtool.site.',

  // FAQ (visible accordion, + SEO + AdSense)
  'faq.title': 'Frequently asked questions',
  'faq.q1': 'Is Shrink really 100% free?',
  'faq.a1': 'Yes, 100% free with no hidden costs. No signup, no email, no credit card, no usage limit. You can compress unlimited images, even hundreds at once, all for free.',
  'faq.q2': 'Are my images uploaded to a server?',
  'faq.a2': 'No. All compression happens in your browser using WebAssembly. Your images never touch any server, not even ours. You can even use Shrink offline after loading the page once. This makes it safer than cloud-based alternatives for sensitive images.',
  'faq.q3': 'Is there a limit on the number of images?',
  'faq.a3': 'No. Drop a hundred images at once if you want. The only limit is 20MB per file. We process them in parallel using a Web Worker pool, then download everything as a single ZIP.',
  'faq.q4': 'How much can images be reduced?',
  'faq.a4': 'Typical reduction is 50-80%. Screenshots and UI images with large areas of solid color can reach 80% or more. Photos usually reduce 40-65%. The exact reduction depends on the image content, not just the format.',
  'faq.q5': 'Which formats are supported?',
  'faq.a5': 'PNG, JPEG, and WebP. The output keeps the same format as the input — we never convert between formats. Each format is compressed with a dedicated engine optimized for it: PNG uses libimagequant, JPEG uses MozJPEG, WebP uses libwebp.',
  'faq.q6': 'Why should I reduce image size for my website?',
  'faq.a6': 'Smaller images load faster. Google uses page speed (Core Web Vitals) as a ranking factor, and images are usually the biggest bottleneck. Reducing your images improves Largest Contentful Paint (LCP), reduces bounce rate, and boosts SEO. Typical sites see 1-3 seconds faster load times after reducing images.',
  'faq.q7': 'Why reduce image size before sending by email?',
  'faq.a7': 'Gmail caps attachments at 25MB. A single uncompressed photo can easily be 8-15MB, so you can only attach 2-3 photos per email. Reduced images let you send entire albums in one email. The recipient also gets them faster, and your sent folder stays smaller.',
  'faq.q8': 'Will the reduced images look different?',
  'faq.a8': 'PNG and WebP reduction uses smart quantization that preserves the visual quality. Most people cannot tell the difference at default settings, even when zooming in. JPEG uses MozJPEG at quality 80, which is the sweet spot for perceived quality vs file size.',
};
